/**
 * GeometryUtils - Pure geometry functions usable by any tool.
 *
 * These are stateless functions with no viewer or tool dependencies.
 */

import * as THREE from 'three';

/** Project a point onto a line defined by origin and direction. */
export function projectOnLine(point: THREE.Vector3, lineOrigin: THREE.Vector3, lineDirection: THREE.Vector3): THREE.Vector3 {
  const dir = lineDirection.clone().normalize();
  const v = point.clone().sub(lineOrigin);
  const t = v.dot(dir);
  return lineOrigin.clone().add(dir.multiplyScalar(t));
}

/**
 * Find the intersection point of two lines in 3D (on a plane).
 * Line 1: p1 + t * d1, Line 2: p2 + s * d2.
 * Returns null if lines are parallel.
 */
export function findLineIntersection(p1: THREE.Vector3, d1: THREE.Vector3, p2: THREE.Vector3, d2: THREE.Vector3, planeNormal: THREE.Vector3): THREE.Vector3 | null {
  const cross = new THREE.Vector3().crossVectors(d1, d2);
  if (cross.length() < 0.0001) return null;

  const p1p2 = new THREE.Vector3().subVectors(p2, p1);

  const u = d1.clone().normalize();
  const v = new THREE.Vector3().crossVectors(planeNormal, u).normalize();

  const d1u = d1.dot(u);
  const d1v = d1.dot(v);
  const d2u = d2.dot(u);
  const d2v = d2.dot(v);
  const p1p2u = p1p2.dot(u);
  const p1p2v = p1p2.dot(v);

  const denom = d1u * d2v - d1v * d2u;
  if (Math.abs(denom) < 0.0001) return null;

  const t = (p1p2u * d2v - p1p2v * d2u) / denom;

  return p1.clone().add(d1.clone().multiplyScalar(t));
}
